import * as PIXI from "pixi.js-legacy"

import * as constants from "./constants"
import * as Utils from "./utils"

export default class Page {

	get transitionForward() { return this._transitionForward }

	get transitionBackward() { return this._transitionBackward }

	// In a page that is larger than the viewport, isAtStart = true on reaching the page going forward
	// (while a page that is not larger than the viewport is considered to be always at its start)
	get isAtStart() { return (this._progress === null || this._progress === 0) }

	// In a page larger than the viewport, isAtEnd = true before leaving the page going forward
	// (while a page that is not larger than the viewport is considered to be always at its end)
	get isAtEnd() { return (this._progress === null || this._progress === 1) }

	// When the page is jumping between two snap points, isAutoScrolling === true
	get isAutoScrolling() {
		return (this._snapPointJumpData) ? this._snapPointJumpData.isAutoScrolling : false
	}

	constructor(player, readingProgression) {
		this._player = player
		this._readingProgression = readingProgression

		// Add an empty _rawSnapPointsArray to hold the values specified in the linkObjects
		// A real _snapPointsArray will then be computed if needed (i.e. only if the page
		// is larger than the viewport, which implies checking this after each resize)
		this._rawSnapPointsArray = []
		this._snapPointsArray = null // This array will also include progress values for each

		this._pageContainer = new PIXI.Container()
		this._slicesArray = []

		this._progress = null
		this._totalDistanceToCover = 0

		this._transitionForward = null
		this._transitionBackward = null

		this._reset()

		this._setRelativeStartAndEnd()
	}

	_reset() {
		this._snapPointJumpData = {
			isAutoScrolling: false,
			shouldForceToEnd: false,
			shouldForceToBacktrack: false,
		}
	}

	_setRelativeStartAndEnd() {
		// Based on the story's reading progression, express the viewport "start"
		// and "end" points in relative coordinates (from the top left corner)
		// (used in _getAbsoluteCenter, for both snap points and goToSliceIndex)
		this._relativeStart = null
		this._relativeEnd = null
		switch (this._readingProgression) {
		case "ltr":
			this._relativeStart = { x: 0, y: 0.5 } // Start is the middle left point
			this._relativeEnd = { x: 1, y: 0.5 } // End is the middle right point
			break
		case "rtl":
			this._relativeStart = { x: 1, y: 0.5 }
			this._relativeEnd = { x: 0, y: 0.5 }
			break
		case "ttb":
			this._relativeStart = { x: 0.5, y: 0 }
			this._relativeEnd = { x: 0.5, y: 1 }
			break
		case "btt":
			this._relativeStart = { x: 0.5, y: 1 }
			this._relativeEnd = { x: 0.5, y: 0 }
			break
		default:
			break
		}
	}

	setTransition(direction, transition) {
		switch (direction) {
		case "forward":
			this._transitionForward = transition
			break
		case "backward":
			this._transitionBackward = transition
			break
		default:
			break
		}
	}

	addSlice(slice) {
		if (!slice) {
			return
		}
		this._slicesArray.push(slice)
		// Add the slice's container to the page container
		slice.addSliceContainerToPageContainer(this._pageContainer)
	}

	addSnapPoints(newSnapPointsArray) {
		if (!newSnapPointsArray || newSnapPointsArray.length === 0) {
			return
		}
		newSnapPointsArray.forEach((snapPointInfo) => {
			const { viewport, x, y } = snapPointInfo
			if ((viewport === "start" || viewport === "center" || viewport === "end")
				&& (x !== null || y !== null)) {
				const snapPoint = {
					sliceIndex: this._slicesArray.length - 1,
					viewport,
					x,
					y,
				}
				this._rawSnapPointsArray.push(snapPoint)
			}
		})
	}

	// Add a texture to each slice once resources have been loaded
	finishBuildingSlices() {
		this._slicesArray.forEach((slice) => {
			slice.finishBuilding()
		})
	}

	pushContentIntoContainer(container) {
		container.addChild(this._pageContainer)
	}

	// Play video slices if needed
	play() {
		this._slicesArray.forEach((slice) => {
			slice.play()
		})
	}

	// Play video slices if needed
	stop() {
		this._slicesArray.forEach((slice) => {
			slice.stop()
		})
	}

	_positionSlices() {
		let sliceOffset = 0

		// For each slice, translate it (in the _pageContainer, via container.position) half its size
		// plus the sum of all previous slice dimensions (so that the first one is translated only
		// half its size and all others are then glued next to it, one after the other)
		this._slicesArray.forEach((slice) => {
			const { size, container } = slice
			if (!container) {
				return
			}
			const { width, height } = size
			switch (this._readingProgression) {
			case "ltr":
				slice.offset = { x: sliceOffset, y: 0 }
				container.position.x = sliceOffset + width / 2
				sliceOffset += width
				break
			case "rtl":
				slice.offset = { x: sliceOffset, y: 0 }
				container.position.x = sliceOffset - width / 2
				sliceOffset -= width
				break
			case "ttb":
				slice.offset = { x: 0, y: sliceOffset }
				container.position.y = sliceOffset + height / 2
				sliceOffset += height
				break
			case "btt":
				slice.offset = { x: 0, y: sliceOffset }
				container.position.y = sliceOffset - height / 2
				sliceOffset -= height
				break
			default:
				break
			}
		})

		this._positionPageContainer(sliceOffset)
	}

	// At the end of the previous operation (_positionSlices):
	// - If the total length of all slices together is less than the viewport dimension
	// (_isLargerThanViewport = false), then move the pageContainer back half the size
	// of its first slice so that things look centered (especially for a "small" unique slice)
	// - If not (_isLargerThanViewport = true), record the start and end positions
	// that will allow the pageContainer to span the whole length of the resources
	// (Also, do note that small pixel errors are accounted for)
	_positionPageContainer(sliceOffset) {
		this._startPosition = { x: 0, y: 0 }
		this._endPosition = { x: 0, y: 0 }
		let totalDistanceToCover = sliceOffset

		const { viewportSize } = this._player
		const { width, height } = viewportSize
		switch (this._readingProgression) {
		case "ltr":
			totalDistanceToCover -= width
			if (totalDistanceToCover <= constants.possiblePixelError) {
				this._isLargerThanViewport = false
				this._pageContainer.position.x = -totalDistanceToCover / 2 - width / 2
			} else {
				this._isLargerThanViewport = true
				this._startPosition.x = -width / 2
				this._endPosition.x = -totalDistanceToCover - width / 2
			}
			break
		case "rtl":
			totalDistanceToCover += width
			if (totalDistanceToCover >= -constants.possiblePixelError) {
				this._isLargerThanViewport = false
				this._pageContainer.position.x = -totalDistanceToCover / 2 + width / 2
			} else {
				this._isLargerThanViewport = true
				this._startPosition.x = width / 2
				this._endPosition.x = -totalDistanceToCover + width / 2
			}
			break
		case "ttb":
			totalDistanceToCover -= height
			if (totalDistanceToCover <= constants.possiblePixelError) {
				this._isLargerThanViewport = false
				this._pageContainer.position.x = -totalDistanceToCover / 2 - height / 2
			} else {
				this._startPosition.y = -height / 2
				this._isLargerThanViewport = true
				this._endPosition.y = -totalDistanceToCover - height / 2
			}
			break
		case "btt":
			totalDistanceToCover += height
			if (totalDistanceToCover >= -constants.possiblePixelError) {
				this._isLargerThanViewport = false
				this._pageContainer.position.x = -totalDistanceToCover / 2 + height / 2
			} else {
				this._isLargerThanViewport = true
				this._startPosition.y = -height / 2
				this._endPosition.y = -totalDistanceToCover + height / 2
			}
			break
		default:
			break
		}

		// At this stage, the totalDistanceToCover is equal to the total length
		// of all slices together minus the relevant viewport dimension (width or height)
		// (with a sign linked to the readingProgression: "-" in rtl or btt, "+" otherwise)
		this._totalDistanceToCover = totalDistanceToCover

		// If there are snap points to build (i.e. progress values to compute), do it
		if (this._isLargerThanViewport === true) {
			this._buildRelevantSnapPoints()
		} else {
			this._snapPointsArray = null
		}
	}

	// Build relevant snap points by adding a progress value
	_buildRelevantSnapPoints() {
		const snapPointsWithProgressArray = []
		let lastProgress = -1
		this._rawSnapPointsArray.forEach((snapPoint) => {
			const progress = this._getProgressForSnapPoint(snapPoint)
			if (progress !== null && progress > lastProgress) {
				const snapPointWithProgress = { ...snapPoint, progress }
				snapPointsWithProgressArray.push(snapPointWithProgress)
				lastProgress = progress
			}
		})
		this._snapPointsArray = snapPointsWithProgressArray
	}


	_getProgressForSnapPoint(snapPoint) {
		const totalDistanceToCover = Math.abs(this._totalDistanceToCover)
		if (totalDistanceToCover <= constants.possiblePixelError) {
			return null
		}

		const {
			sliceIndex,
			viewport,
			x,
			y,
		} = snapPoint
		const slice = this._slicesArray[sliceIndex]
		const { size, offset } = slice

		// Get the absolute coordinates of the viewport's center for that snap point
		const absoluteCenter = this._getAbsoluteCenter(viewport, { x, y }, size)
		if (!absoluteCenter) {
			return null
		}
		absoluteCenter.x -= offset.x
		absoluteCenter.y -= offset.y

		// Compute the distance from the pageContainer's start point to that new point
		const distanceToCenter = Utils.getDistance(this._startPosition, absoluteCenter)
		// Convert that value into an acceptable progress value (between 0 and 1)
		const progress = Math.min(Math.max(Math.abs(distanceToCenter / totalDistanceToCover), 0), 1)
		return progress
	}

	// Get the coordinates of the viewport's center corresponding to a certain snap point alignment
	_getAbsoluteCenter(viewportPoint, coords, sliceSize) {
		const { viewportSize } = this._player

		const sign = (this._readingProgression === "ltr" || this._readingProgression === "ttb") ? -1 : 1
		const x = Utils.parseCoordinate(coords.x, sliceSize.width)
		const y = Utils.parseCoordinate(coords.y, sliceSize.height)
		if (x === null || y === null) {
			return null
		}

		const center = {
			x: sign * x,
			y: sign * y,
		}
		switch (viewportPoint) {
		case "start":
			center.x += (this._relativeStart.x - 0.5) * viewportSize.width
			center.y += (this._relativeStart.y - 0.5) * viewportSize.height
			break
		case "end":
			center.x += (this._relativeEnd.x - 0.5) * viewportSize.width
			center.y += (this._relativeEnd.y - 0.5) * viewportSize.height
			break
		default: // "center"
			break
		}
		return center
	}

	// Position the _pageContainer to conform to the specified progress value
	_setProgress(p) {
		this._progress = p
		const vector = {
			x: this._endPosition.x - this._startPosition.x,
			y: this._endPosition.y - this._startPosition.y,
		}
		this._pageContainer.position.x = this._startPosition.x + p * vector.x
		this._pageContainer.position.y = this._startPosition.y + p * vector.y
	}

	// Apply the amount of user scrolling to the _pageContainer's position
	// by computing what new progress value the delta corresponds to
	applyScrollDelta(scrollDelta) {
		if (this._progress === null) {
			return
		}
		const { deltaX, deltaY } = scrollDelta
		let progress = this._progress
		const xDistanceToCover = this._endPosition.x - this._startPosition.x
		if (xDistanceToCover > 0) {
			const xPosition = Math.min(Math.max(this._pageContainer.position.x - deltaX,
				this._startPosition.x), this._startPosition.x + xDistanceToCover)
			progress = Math.abs((xPosition - this._startPosition.x) / xDistanceToCover)
		} else if (xDistanceToCover < 0) {
			const xPosition = Math.min(Math.max(this._pageContainer.position.x - deltaX,
				this._startPosition.x + xDistanceToCover), this._startPosition.x)
			progress = Math.abs((xPosition - this._startPosition.x) / xDistanceToCover)
		}
		const yDistanceToCover = this._endPosition.y - this._startPosition.y
		if (yDistanceToCover > 0) {
			const yPosition = Math.min(Math.max(this._pageContainer.position.y - deltaY,
				this._startPosition.y), this._startPosition.y + yDistanceToCover)
			progress = Math.abs((yPosition - this._startPosition.y) / yDistanceToCover)
		} else if (yDistanceToCover < 0) {
			const yPosition = Math.min(Math.max(this._pageContainer.position.y - deltaY,
				this._startPosition.y + yDistanceToCover), this._startPosition.y)
			progress = Math.abs((yPosition - this._startPosition.y) / yDistanceToCover)
		}
		this._setProgress(progress)
	}

	goToNextSnapPoint() {
		const targetProgress = this._getNextSnapPointProgress()
		if (targetProgress !== null) {
			this._startSnapPointJump(targetProgress)
		}
	}

	goToPreviousSnapPoint() {
		const targetProgress = this._getPreviousSnapPointProgress()
		if (targetProgress !== null) {
			this._startSnapPointJump(targetProgress)
		}
	}

	// Get the progress value of the next snap point in the list (1 if there is none)
	_getNextSnapPointProgress() {
		if (!this._snapPointsArray) {
			return null
		}
		let i = 0
		while (i < this._snapPointsArray.length
			&& this._snapPointsArray[i].progress <= this._progress) {
			i += 1
		}
		let nextProgress = 1
		if (i < this._snapPointsArray.length) {
			nextProgress = this._snapPointsArray[i].progress
		}
		return nextProgress
	}

	// Get the progress value of the previous snap point in the list (0 if there is none)
	_getPreviousSnapPointProgress() {
		if (!this._snapPointsArray) {
			return null
		}
		let i = this._snapPointsArray.length - 1
		while (i >= 0
			&& this._snapPointsArray[i].progress >= this._progress) {
			i -= 1
		}
		let previousProgress = 0
		if (i >= 0) {
			previousProgress = this._snapPointsArray[i].progress
		}
		return previousProgress
	}

	_startSnapPointJump(targetProgress, mode) {
		this._snapPointJumpData = {
			isAutoScrolling: true,
			isGoingForward: (targetProgress - this._progress >= 0),
			targetProgress,
			startProgress: this._progress,
			startDate: Date.now(),
			mode,
		}
		requestAnimationFrame(this._autoProgress.bind(this))
	}

	goToClosestSnapPoint() {
		if (this._isLargerThanViewport === false) {
			return
		}
		const nextProgress = this._getNextSnapPointProgress()
		const previousProgress = this._getPreviousSnapPointProgress()
		const progressDifferenceToNext = nextProgress - this._progress
		const progressDifferenceToPrevious = this._progress - previousProgress
		let targetProgress = this._progress
		if (progressDifferenceToNext < progressDifferenceToPrevious) {
			targetProgress = nextProgress
		} else if (progressDifferenceToNext > progressDifferenceToPrevious) {
			targetProgress = previousProgress
		}
		this._startSnapPointJump(targetProgress, "sticky")
	}

	_autoProgress() {
		if (this._isAutoScrolling === false) {
			return
		}

		const {
			shouldForceToBacktrack,
			shouldForceToEnd,
			targetProgress,
			startProgress,
			startDate,
			mode,
			endCallback,
		} = this._snapPointJumpData

		const distanceToCover = Math.abs((targetProgress - startProgress) * this._totalDistanceToCover)
		this._changeDuration = (mode === "sticky")
			? distanceToCover / this._stickyMoveSpeed
			: distanceToCover / this._snapJumpSpeed

		const percent = (Date.now() - startDate) / this._changeDuration

		if (shouldForceToBacktrack === true) {
			this._reset()
			if (endCallback) {
				endCallback()
			}
		} else if (percent >= 1 || shouldForceToEnd === true) {
			this._setProgress(targetProgress)
			this._reset()
		} else {
			const forcedProgress = startProgress + (targetProgress - startProgress) * percent
			this._setProgress(forcedProgress)
			requestAnimationFrame(this._autoProgress.bind(this))
		}
	}

	// Force the snap point jump to its end
	forceJump(callback, isGoingForward) {
		if (!this._snapPointJumpData) {
			return
		}
		if (isGoingForward !== this._snapPointJumpData.isGoingForward) {
			// Only run the callback if the movement directions differ
			this._snapPointJumpData.endCallback = callback
			this._snapPointJumpData.shouldForceToBacktrack = true
		} else {
			this._snapPointJumpData.shouldForceToEnd = true
		}
	}

	// Get what slice a given path corresponds to (as an index in the page's _slicesArray)
	getSliceIndexInPageForPath(path) {
		if (!this._slicesArray || this._slicesArray.length === 0) {
			return null
		}
		let hasFoundPath = false
		let sliceIndex = 0
		while (sliceIndex < this._slicesArray.length && hasFoundPath === false) {
			const slice = this._slicesArray[sliceIndex]
			if (slice.path === path) {
				hasFoundPath = true
				return sliceIndex
			}
			sliceIndex += 1
		}
		return null
	}

	goToSliceIndex(sliceIndex, isGoingForward) {
		// If the page is not larger than the viewport, just display it
		// Otherwise...
		if (this._isLargerThanViewport === true) {
			// If a sliceIndex is specified, get the progress value to which it corresponds
			if (sliceIndex !== null) {
				const progress = this._getProgressForSliceIndex(sliceIndex)
				this._setProgress(progress || 0)
			// Otherwise just go to the start or end of the page
			} else {
				this._setProgress((isGoingForward === true) ? 0 : 1)
			}
		}
	}

	_getProgressForSliceIndex(sliceIndex) {
		// The progress value is computed for the "start" viewport point in the case
		// the readingProgression is ltr or btt, and for the "end" point otherwise
		const snapPoint = {
			sliceIndex,
			viewport: "start",
			x: (this._readingProgression === "rtl") ? "100%" : "0%",
			y: (this._readingProgression === "btt") ? "100%" : "0%",
		}
		const progress = this._getProgressForSnapPoint(snapPoint)
		return progress
	}

	resize() {
		this._slicesArray.forEach((slice) => {
			slice.resize()
		})
		// Reposition resized slices so that isLargerThanViewport and snap points get updated
		this._positionSlices()

		// If the page now entirely fits within the viewport
		if (this._isLargerThanViewport === false) {

			// If we are actually jumping between snap points...
			if (this.isAutoScrolling === true) {
				// ... then cancel the jump (forcing a backtrack)
				const doAfterBacktrack = () => { this._progress = null }
				this.forceJump(doAfterBacktrack, !this._snapPointJumpData.isGoingForward)
			}

		// Otherwise keep the existing progress value if there was one, otherwise start at 0
		} else {
			this._setProgress(this._progress || 0)
		}

		// Update snap point speeds based on readingProgression
		const { viewportSize } = this._player
		const { width, height } = viewportSize
		if (this._readingProgression === "ltr" || this._readingProgression === "rtl") {
			this._snapJumpSpeed = width * constants.snapJumpSpeedFactor
			this._stickyMoveSpeed = width * constants.stickyMoveSpeedFactor
		} else {
			this._snapJumpSpeed = height * constants.snapJumpSpeedFactor
			this._stickyMoveSpeed = height * constants.stickyMoveSpeedFactor
		}
	}

	destroy() {
		this._slicesArray.forEach((slice) => {
			slice.destroy()
		})
	}

}